import { addMonths, differenceInMonths, formatISO, startOfMonth } from 'date-fns'
import { find, sumBy } from 'lodash'
import { getDiscountPercentage } from './getDiscountPercentage'
import { productsIds } from './enrichSubscriptionUtils'

/**
 * $4 per credit base price
 */
const baseUnitPrice = 4

// Utils
export const round = (number) => {
  if (Number.isInteger(number)) {
    return number
  } else {
    return Math.round(number * 100) / 100
  }
}

export const discountByPercent = (amount, percent) => {
  return round(amount - amount * (percent / 100))
}

export const percentOf = (amount, percent) => {
  return round(amount * (percent / 100))
}

export const startOfThisMonthDate = formatISO(startOfMonth(new Date()), {
  representation: 'date',
})

export const startOfNextMonthDate = formatISO(startOfMonth(addMonths(new Date(), 1)), {
  representation: 'date',
})
/**
 * Calculate contract duration with date order correction
 * @param {String} date1 - First date to compare
 * @param {String} date2 - Second date to compare
 * @returns {number} - The duration of the contract in months, or 0 if dates are invalid
 */
export const contractDuration = (date1, date2) => {
  try {
    const parsedDate1 = new Date(date1)
    const parsedDate2 = new Date(date2)

    // Check if either date is invalid
    if (isNaN(parsedDate1.getTime()) || isNaN(parsedDate2.getTime())) {
      return 0
    }

    // Determine which date is earlier
    const startDate = parsedDate1 <= parsedDate2 ? parsedDate1 : parsedDate2
    const endDate = parsedDate1 > parsedDate2 ? parsedDate1 : parsedDate2

    return differenceInMonths(endDate, startDate)
  } catch (error) {
    return 0
  }
}
/**
 *
 * @param {number} months - Get start and end dates for the given months
 * @returns {object} - The start and end dates for the given months
 */
export const contractDurationDates = (months) => {
  if (!months) return {}
  const startDate = startOfThisMonthDate // Today as the start date
  const endDate = getStartOfMonthDate(months) // Calculate end date by adding months

  return { startDate, endDate }
}
/**
 *
 * @param {number} months - Get the start of the month date for the given months
 * @returns {string} - The start of the month date for the given months
 */
export const getStartOfMonthDate = (months) =>
  formatISO(startOfMonth(addMonths(new Date(), months)), {
    representation: 'date',
  })

export const getTermString = (interval) => {
  let termString = 'mo'
  if (interval === 12) {
    termString = 'yr'
  }
  if (interval === 24) {
    termString = '2 yrs'
  }
  if (interval === 36) {
    termString = '3 yrs'
  }
  return termString
}

/**
 * Calculate and add unit prices to the products, according to the volume, duration, and other discounts.
 * Based on Austen's instruction on Slack:
 *
 * Perhaps it can be as simple as following the spreadsheet, but then up-front and small discount are applied sequentially, in this order:
 *    - Reserved Credit pricing (includes annual/volume discount).
 *    - IF support is ordered, 25% is added to that amount above.
 *    - IF up-front pricing, 10% off is then applied to the resulting amount.
 *    - IF small business discount, 35% is then applied to the resulting amount.
 *
 * References:
 *   - https://serverlessteam.slack.com/archives/C01UH0RRCLV/p1731512121746239?thread_ts=1731451614.124829&cid=C01UH0RRCLV
 *   - https://docs.google.com/spreadsheets/d/1A_zQrzgHJuQJ8yrXa1lZ9dzcajtuirIzKFyHAVGNcXw/
 *
 * @param products - List of products without unitPrice
 * @param isSmallBusiness - Whether the customer is a small business
 *
 * @returns List of products with unitPrice
 */
export const getProductsPrices = (products, isSmallBusiness) => {
  /**
   * If there are no products, return the empty array
   */
  if (!products || !products.length || products.length === 0) {
    return products
  }

  /**
   * The products array that will be returned with unit prices
   */
  const productsWithUnitPrices = []

  const supportProduct = products.find((p) => p.id === 'premium_support_v1' && p.units > 0)

  const reservedProduct = products.find((p) => p.id === 'credits1_reserved_v1' && p.units > 0)

  const unlimitedProduct = products.find((p) => p.id === 'credits_unlimited_v1' && p.units > 0)

  /**
   * Only the overage product could have a units value of zero
   * The overage product is pretty much static
   * If just the id included in the products array
   * the final product should always look like this
   */
  const overageProduct = products.find((p) => p.id === 'credits1_overage_v1')

  /**
   * The starting discount percent is 0 for credits below 15
   * Then below, we calculate the discount percent based on
   * the contract duration and the number of credits
   */
  let discountPercent = 0
  let creditPrice = baseUnitPrice

  const startDate =
    reservedProduct?.startDate ||
    overageProduct?.startDate ||
    unlimitedProduct?.startDate ||
    supportProduct?.startDate
  const endDate =
    reservedProduct?.endDate ||
    overageProduct?.endDate ||
    unlimitedProduct?.endDate ||
    supportProduct?.endDate
  const interval =
    reservedProduct?.interval ||
    overageProduct?.interval ||
    unlimitedProduct?.interval ||
    supportProduct?.interval

  const units =
    reservedProduct?.units ||
    overageProduct?.units ||
    unlimitedProduct?.units ||
    supportProduct?.units
  const term = contractDuration(endDate, startDate)

  if (overageProduct) {
    productsWithUnitPrices.push({
      id: 'credits1_overage_v1',
      units: 0,
      unitSize: 1,
      unitPrice: baseUnitPrice,
      interval: 1,
      startDate: startOfThisMonthDate,
      endDate: startOfNextMonthDate,
      autoRenew: true,
    })
  }
  if (unlimitedProduct) {
    unlimitedProduct.total = unlimitedProduct.unitPrice * unlimitedProduct.units
    productsWithUnitPrices.push({
      id: 'credits_unlimited_v1',
      units: unlimitedProduct.units,
      unitSize: unlimitedProduct.unitSize,
      unitPrice: unlimitedProduct.unitPrice,
      interval: unlimitedProduct.interval,
      startDate: unlimitedProduct.startDate,
      endDate: unlimitedProduct.endDate,
      total: unlimitedProduct.total,
      autoRenew: true,
    })
  }

  /**
   * Most of the calculations are based on the reserved product
   * so only do the calculations if they reserved credits
   * The premium support product also depends on having reserved credits
   */
  if (reservedProduct) {
    const { units } = reservedProduct

    /**
     * If monthly contract
     */
    if (term === 1) {
      if (units > 0 && units < 50) discountPercent = 0
      if (units >= 50 && units < 300) discountPercent = 12.5
      if (units >= 300) discountPercent = 37.5
    }

    /**
     * If one year contract
     */
    if (term === 12) {
      if (units >= 0 && units < 15) discountPercent = 0
      if (units >= 15 && units < 50) discountPercent = 20
      if (units >= 50 && units < 100) discountPercent = 29
      if (units >= 100 && units < 200) discountPercent = 30
      if (units >= 200 && units < 300) discountPercent = 40
      if (units >= 300 && units < 3000) discountPercent = 50
      if (units >= 3000 && units < 7000) discountPercent = 55
      if (units >= 7000 && units < 12000) discountPercent = 60
      if (units >= 12000) discountPercent += 65
    }

    /**
     * If two year contract
     */
    if (term === 24) {
      if (units >= 0 && units < 15) discountPercent = 0
      if (units >= 15 && units < 50) discountPercent = 22
      if (units >= 50 && units < 100) discountPercent = 32
      if (units >= 100 && units < 200) discountPercent = 35
      if (units >= 200 && units < 300) discountPercent = 45
      if (units >= 300 && units < 3000) discountPercent = 55
      if (units >= 3000 && units < 7000) discountPercent = 60
      if (units >= 7000 && units < 12000) discountPercent = 65
      if (units >= 12000) discountPercent = 68
    }

    /**
     * If three year contract
     */
    if (term === 36) {
      if (units >= 0 && units < 15) discountPercent = 0
      if (units >= 15 && units < 50) discountPercent = 25
      if (units >= 50 && units < 100) discountPercent = 35
      if (units >= 100 && units < 200) discountPercent = 40
      if (units >= 200 && units < 300) discountPercent = 50
      if (units >= 300 && units < 3000) discountPercent = 60
      if (units >= 3000 && units < 7000) discountPercent = 65
      if (units >= 7000 && units < 12000) discountPercent = 70
      if (units >= 12000) discountPercent = 72
    }

    /**
     * Apply the discount to the base unit price
     * and get the discounted unit price
     */
    creditPrice = discountByPercent(baseUnitPrice, discountPercent)
    reservedProduct.creditPrice = creditPrice

    /**
     * Now we can discount further based if they opted for upfront payment
     * It only applies only if the interval is at least 12 months
     * It's always 10% regardless of the interval (12, 24, or 36 months)
     */
    if (reservedProduct.interval >= 12) {
      creditPrice = discountByPercent(creditPrice, 10)
      reservedProduct.creditPrice = creditPrice
    }

    /**
     * If they are a small business, they get an additional 35% discount
     * on top of the upfront discount, according to the required order..
     */
    if (isSmallBusiness) {
      creditPrice = discountByPercent(creditPrice, 35)
      reservedProduct.creditPrice = creditPrice
    }

    /**
     * Only add the support product if:
     *   - User opted for premium support
     *   - They reserved at least 3,000 credits
     *   - They have a contract term of at least 12 months
     *
     * References: https://serverlessteam.slack.com/archives/C01UH0RRCLV/p1731443912754169
     */
    const shouldAddSupportProduct = supportProduct && reservedProduct.units >= 3000 && term >= 12
    reservedProduct.unitPrice = creditPrice
    reservedProduct.total = creditPrice * reservedProduct.units
    if (shouldAddSupportProduct) {
      /**
       * The support product price is 25% of the reserved product price.
       * We calculate the percentage here before we add further discounts
       * like upfront and small business, following the required order.
       */
      supportProduct.units = reservedProduct.units
      supportProduct.unitPrice = percentOf(
        reservedProduct.unitPrice,
        25 // 25% support price
      )
    }

    if (reservedProduct.interval >= 12) {
      reservedProduct.total = reservedProduct.total * reservedProduct.interval
    }
    /**
     * Now we are done with the reserved product, we can add it to the result
     */
    productsWithUnitPrices.push({
      id: reservedProduct.id,
      units: reservedProduct.units,
      unitSize: reservedProduct.unitSize,
      unitPrice: reservedProduct.unitPrice,
      creditPrice: reservedProduct.creditPrice,
      interval: reservedProduct.interval,
      startDate: reservedProduct.startDate,
      endDate: reservedProduct.endDate,
      autoRenew: reservedProduct.autoRenew,
      total: reservedProduct.total,
    })

    /**
     * If the user opted for premium support, we apply the same discounts
     * to the support product, in the same order, and add the product
     */
    if (shouldAddSupportProduct) {
      /**
       * Upfront discount only applies if the interval is at least 12 months
       */
      if (supportProduct.interval >= 12) {
        supportProduct.unitPrice = discountByPercent(
          supportProduct.unitPrice,
          10 // 10% upfront discount
        )
      }

      /**
       * Small business discount
       */
      if (isSmallBusiness) {
        supportProduct.unitPrice = discountByPercent(
          supportProduct.unitPrice,
          35 // 35% small business discount
        )
      }

      supportProduct.total = supportProduct.unitPrice * supportProduct.units
      if (supportProduct.interval >= 12) {
        supportProduct.total = supportProduct.total * supportProduct.interval
      }
      /**
       * Finally, add the support product to the result
       */
      productsWithUnitPrices.push({
        id: supportProduct.id,
        unitSize: 1,
        units: supportProduct.units,
        unitPrice: supportProduct.unitPrice,
        interval: supportProduct.interval,
        startDate: supportProduct.startDate,
        endDate: supportProduct.endDate,
        autoRenew: supportProduct.autoRenew,
        total: supportProduct.total,
      })
    }
  }

  let total = sumBy(productsWithUnitPrices, (n) => n.total)
  let totalCredits = units

  if (unlimitedProduct) {
    totalCredits = 'unlimited'
  }
  if (reservedProduct) {
    totalCredits = reservedProduct.units
  }
  const termString = getTermString(interval)

  const recurring = {
    term,
    interval,
  }

  const totalDiscountPercentage = getDiscountPercentage(baseUnitPrice, creditPrice)
  return {
    products: productsWithUnitPrices,
    discountPercent,
    creditPrice, // Extracted credit price
    total,
    totalCredits,
    termString,
    recurring,
    startDate,
    endDate,
    reservedProduct,
    overageProduct,
    unlimitedProduct,
    supportProduct,
    totalDiscountPercentage,
  }
}

/**
 * Get the overage product price otherwise return the base price
 * @param {object} subscription - The subscription object
 * @returns {number} - The overage product price
 */
export const getOverageProductPrice = (subscription) => {
  const overageProduct = find(subscription?.products, (p) => p.id === productsIds?.overage)
  return overageProduct?.unitPrice || baseUnitPrice
}
